import { useMemo, useState } from 'react'
import { Box, Flex, Grid, Text, VStack } from '@chakra-ui/react'
import { LuArrowRight, LuExternalLink, LuHandshake, LuInfo, LuPackage } from 'react-icons/lu'
import { DataList, DataTable, PageHeader, toaster } from '@minetrace/ui'
import { brand, themeColors } from '../../theme'

/**
 * Directorio de Comercializadores — MOCK para la demo de Kuntur Comercializadores.
 *
 * No hay backend: los datos son ficticios y están alineados con el mock de
 * apps/comercializador (mismos códigos madre e hijo), para mostrar cómo la
 * planta ve, desde Kuntur, el lote madre que despacha un comercializador y los
 * códigos hijo que trae dentro. La entrada de menú la inyecta
 * `auth/mockMenuItems.ts`.
 */

const PORTAL_URL = 'https://comercializador.kuntur.com.pe'

interface Hijo { code: string; minero: string; dm: string; tmh: number; leyAu: number; humedad: number }
interface LoteMadre { code: string; despacho: string; estado: 'Recibido en planta' | 'En tránsito'; hijos: Hijo[] }
interface Comercializador {
  id: string
  ruc: string
  razonSocial: string
  ubicacion: string
  mineros: number
  estado: 'Aprobado' | 'En evaluación'
  conectado: boolean
  registro: string
  lotesMadre: LoteMadre[]
}

const DATA: Comercializador[] = [
  {
    id: 'C1', ruc: '20612345678', razonSocial: 'Comercializadora Minera Andina del Sur S.A.C.', ubicacion: 'Chala, Caravelí — Arequipa',
    mineros: 8, estado: 'Aprobado', conectado: true, registro: '2026-08-21',
    lotesMadre: [
      {
        code: 'LM-CAS-2610-0002', despacho: '2026-10-16', estado: 'En tránsito', hijos: [
          { code: 'KTR-261006003128', minero: 'Asociación de Mineros Artesanales Nueva Esperanza', dm: 'Esperanza I', tmh: 10.9, leyAu: 13.5, humedad: 6.8 },
          { code: 'KTR-261008005560', minero: 'Minera Aurífera El Cóndor E.I.R.L.', dm: 'El Cóndor de Oro', tmh: 9.3, leyAu: 22.4, humedad: 5.9 },
          { code: 'KTR-261009002284', minero: 'Minera Artesanal Santa Rosa de Chala E.I.R.L.', dm: 'Santa Rosa 2008', tmh: 11.6, leyAu: 15.1, humedad: 6.0 },
          { code: 'KTR-261010006613', minero: 'Rosa Mamani Ccori', dm: 'Rosita de Yauca', tmh: 6.8, leyAu: 17.3, humedad: 6.5 },
        ],
      },
      {
        code: 'LM-CAS-2610-0001', despacho: '2026-10-01', estado: 'Recibido en planta', hijos: [
          { code: 'KTR-260926004417', minero: 'Minera Artesanal Santa Rosa de Chala E.I.R.L.', dm: 'Santa Rosa 2008', tmh: 12.4, leyAu: 14.8, humedad: 6.2 },
          { code: 'KTR-260928001902', minero: 'Juan Quispe Huamaní', dm: 'Virgen del Carmen 3', tmh: 8.1, leyAu: 19.6, humedad: 5.4 },
          { code: 'KTR-260930007731', minero: 'Cooperativa Minera Señor de Huanca', dm: 'Señor de Huanca II', tmh: 15.7, leyAu: 11.2, humedad: 7.1 },
        ],
      },
    ],
  },
  {
    id: 'C2', ruc: '20609876541', razonSocial: 'Inversiones Comerciales Minerales del Pacífico E.I.R.L.', ubicacion: 'Nasca — Ica',
    mineros: 5, estado: 'Aprobado', conectado: false, registro: '2026-09-10', lotesMadre: [],
  },
  {
    id: 'C3', ruc: '20614455662', razonSocial: 'Acopiadora Aurífera Sur Medio S.A.C.', ubicacion: 'Atico, Caravelí — Arequipa',
    mineros: 3, estado: 'En evaluación', conectado: false, registro: '2026-10-09', lotesMadre: [],
  },
]

const ESTADO_BADGE: Record<string, { bg: string; color: string }> = {
  'Aprobado': { bg: 'rgba(42, 138, 135, 0.18)', color: '#1d6664' },
  'En evaluación': { bg: 'rgba(61, 111, 168, 0.2)', color: '#3d6fa8' },
  'Recibido en planta': { bg: 'rgba(42, 138, 135, 0.18)', color: '#1d6664' },
  'En tránsito': { bg: 'rgba(61, 111, 168, 0.2)', color: '#3d6fa8' },
}

function Badge({ label }: { label: string }) {
  const s = ESTADO_BADGE[label] ?? { bg: 'rgba(181, 170, 157, 0.2)', color: '#857b71' }
  return (
    <Box display="inline-block" px={2} py={0.5} borderRadius="md" fontSize="10px" fontWeight="bold" textTransform="uppercase"
      letterSpacing="0.05em" bg={s.bg} color={s.color} whiteSpace="nowrap">
      {label}
    </Box>
  )
}

const fmt = (n: number, d = 2) => n.toLocaleString('es-PE', { minimumFractionDigits: d, maximumFractionDigits: d })
const fmtDate = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('es-PE')

export default function ComercializadoresPage() {
  const colors = themeColors
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string>('C1')

  const data = useMemo(() => {
    const q = search.trim().toLowerCase()
    return q ? DATA.filter(d => d.razonSocial.toLowerCase().includes(q) || d.ruc.includes(q)) : DATA
  }, [search])
  const selected = DATA.find(d => d.id === selectedId)

  return (
    <VStack gap={6} alignItems="stretch">
      <PageHeader
        title="Directorio de Comercializadores"
        subtitle="Comercializadores que acopian mineral de varios mineros y lo despachan a planta con un código madre"
        buttonLabel="Nuevo comercializador"
        buttonIcon={<LuHandshake size={18} />}
        onButtonClick={() => toaster.info({ description: 'El registro de comercializadores estará disponible en la siguiente versión.' })}
        colors={colors}
      />

      <Flex gap={3} align="flex-start" p={4} borderRadius="lg" bg="rgba(47, 122, 109, 0.08)" border="1px solid rgba(47, 122, 109, 0.3)">
        <Box color="#2f7a6d" mt={0.5} flexShrink={0}><LuInfo size={18} /></Box>
        <Text fontSize="sm" color={colors.textSecondary}>
          <b>Módulo en demostración.</b> Cuando un comercializador despacha un <b>lote madre</b>, la planta lo recibe con los
          <b> códigos hijo</b> que lo componen: cada uno conserva su minero, su derecho minero y su expediente. Los comercializadores
          operan su propio portal en{' '}
          <a href={PORTAL_URL} target="_blank" rel="noreferrer" style={{ color: '#2f7a6d', fontWeight: 600 }}>comercializador.kuntur.com.pe</a>.
        </Text>
      </Flex>

      <DataList<Comercializador>
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por RUC o razón social..."
        page={1}
        totalPages={1}
        onPageChange={() => {}}
        totalCount={data.length}
        entityName="comercializadores"
        brandColor={brand.green}
        headers={['RUC', 'Razón Social', 'Ubicación', 'Mineros de origen', 'Lotes madre', 'Portal Kuntur', 'Estado', 'Fecha Registro']}
        data={data}
        colors={colors}
        keyExtractor={d => d.id}
        renderRow={d => (
          <>
            <DataTable.Cell><Text fontFamily="monospace" fontSize="sm" color={colors.text}>{d.ruc}</Text></DataTable.Cell>
            <DataTable.Cell><Text fontSize="sm" fontWeight="500" color={colors.text}>{d.razonSocial}</Text></DataTable.Cell>
            <DataTable.Cell><Text fontSize="xs" color={colors.text}>{d.ubicacion}</Text></DataTable.Cell>
            <DataTable.Cell><Text fontSize="sm" fontWeight="600" color={colors.text} textAlign="center">{d.mineros}</Text></DataTable.Cell>
            <DataTable.Cell><Text fontSize="sm" fontWeight="600" color={colors.text} textAlign="center">{d.lotesMadre.length}</Text></DataTable.Cell>
            <DataTable.Cell>
              <Text fontSize="xs" fontWeight="600" color={d.conectado ? '#1d6664' : colors.textMuted}>{d.conectado ? 'Conectado' : 'Pendiente'}</Text>
            </DataTable.Cell>
            <DataTable.Cell><Badge label={d.estado} /></DataTable.Cell>
            <DataTable.Cell><Text fontSize="xs" color={colors.text}>{fmtDate(d.registro)}</Text></DataTable.Cell>
          </>
        )}
        actions={d => [
          { label: 'Ver lotes madre recibidos', onClick: () => setSelectedId(d.id) },
          ...(d.conectado ? [{ label: 'Abrir su portal', onClick: () => window.open(PORTAL_URL, '_blank') }] : []),
        ]}
      />

      {selected && (
        <Box bg={colors.bgPanel} border="1px solid" borderColor={colors.border} borderRadius="xl" overflow="hidden">
          <Flex px={5} py={4} borderBottom="1px solid" borderColor={colors.border} align="center" gap={2}>
            <LuPackage size={18} color={brand.green} />
            <Text fontWeight="700" color={colors.text}>Lotes madre recibidos de {selected.razonSocial}</Text>
          </Flex>
          <Box p={5}>
            {selected.lotesMadre.length === 0 ? (
              <Text fontSize="sm" color={colors.textMuted}>Este comercializador aún no ha despachado lotes madre a la planta.</Text>
            ) : (
              <VStack gap={4} alignItems="stretch">
                {selected.lotesMadre.map(lm => <LoteMadreCard key={lm.code} lm={lm} />)}
              </VStack>
            )}
          </Box>
        </Box>
      )}
    </VStack>
  )
}

function LoteMadreCard({ lm }: { lm: LoteMadre }) {
  const colors = themeColors
  const tms = (h: Hijo) => h.tmh * (1 - h.humedad / 100)
  const total = lm.hijos.reduce((s, h) => s + h.tmh, 0)
  const totalTms = lm.hijos.reduce((s, h) => s + tms(h), 0)
  const ley = lm.hijos.reduce((s, h) => s + tms(h) * h.leyAu, 0) / totalTms

  return (
    <Box border="1px solid" borderColor={colors.border} borderRadius="lg" overflow="hidden">
      <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} direction={{ base: 'column', md: 'row' }} gap={3}
        px={4} py={3} bg={colors.bgCard}>
        <Flex align="center" gap={3} wrap="wrap">
          <Text fontFamily="monospace" fontWeight="800" color={colors.text}>{lm.code}</Text>
          <Badge label={lm.estado} />
          <Text fontSize="xs" color={colors.textMuted}>Despacho {fmtDate(lm.despacho)} · {lm.hijos.length} códigos hijo · {fmt(total)} TMH · ley pond. {fmt(ley)} g/t</Text>
        </Flex>
        <Flex as="a" {...{ href: `${PORTAL_URL}/verificar/${lm.code}`, target: '_blank', rel: 'noreferrer' }} align="center" gap={1.5}
          fontSize="sm" fontWeight="600" color={brand.green} _hover={{ textDecoration: 'underline' }}>
          Ver trazabilidad completa <LuExternalLink size={14} />
        </Flex>
      </Flex>
      <Grid templateColumns={{ base: '1fr', lg: '1fr auto 220px' }} gap={4} p={4} alignItems="center">
        <VStack gap={2} alignItems="stretch">
          {lm.hijos.map(h => (
            <Flex key={h.code} justify="space-between" gap={3} align="center" px={3} py={2} borderRadius="md" borderLeft="3px solid" borderLeftColor="#3b6bb3" bg={colors.bgCard}>
              <Box minW={0}>
                <Text fontFamily="monospace" fontSize="xs" fontWeight="700" color={colors.text}>{h.code}</Text>
                <Text fontSize="sm" color={colors.text}>{h.minero}</Text>
                <Text fontSize="xs" color={colors.textMuted}>D.M. {h.dm} · {fmt(h.tmh)} TMH · {fmt(h.leyAu, 1)} g/t</Text>
              </Box>
              <Text fontSize="md" fontWeight="800" color="#2f7a6d" flexShrink={0}>{fmt((tms(h) / totalTms) * 100, 1)}%</Text>
            </Flex>
          ))}
        </VStack>
        <Box color={colors.textMuted} display={{ base: 'none', lg: 'block' }}><LuArrowRight size={22} /></Box>
        <Box bg="#1b1a17" color="white" borderRadius="lg" p={4}>
          <Text fontSize="10px" letterSpacing="0.1em" color="#7fbfb1" fontWeight="700">CÓDIGO MADRE</Text>
          <Text fontFamily="monospace" fontWeight="800">{lm.code}</Text>
          <Text fontSize="xs" color="rgba(255,255,255,0.7)" mt={2}>
            Al recibirlo, la planta hereda el expediente de los {lm.hijos.length} mineros de origen.
          </Text>
        </Box>
      </Grid>
    </Box>
  )
}
